/**
 * Client-side contract for the guardrails warn/block override flow.
 *
 * Mirrors the backend shapes (no generated types exist for these):
 * - error body: `server.api_commons.add_reasons_and_guardrails_body`
 * - violation:  `server.modules.guardrails.schemas.guardrail_schemas.GuardrailViolation`
 * - token:      `server.modules.guardrails.schemas.guardrail_schemas.GuardrailOverrideToken`
 */

/** Request header carrying the override token (backend: `override_context.OVERRIDE_HEADER`). */
export const OVERRIDE_HEADER = 'X-Guardrail-Override';

/** Narrows an `unknown` to a plain object so callers can safely read its keys without `as`. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Canonical UUID, the only shape `attempt_id` is accepted in. Mirrors the backend's
 * `GuardrailOverrideToken._keep_only_a_well_formed_attempt_id`, which parses the value as a
 * UUID and normalizes its spelling: the id is the join key for every decision and outcome row,
 * so an unbounded string on the wire is a warehouse-correctness problem rather than a cosmetic
 * one. Anything else is dropped rather than rejected — the whole override flow is fail-open, and
 * refusing the token over a malformed correlation id would deny the user their override.
 */
const ATTEMPT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The `attempt_id` in `value`, lower-cased, or `undefined` when it is absent or malformed. */
export const parseAttemptId = (value: unknown): string | undefined =>
  typeof value === 'string' && ATTEMPT_ID_PATTERN.test(value) ? value.toLowerCase() : undefined;

/**
 * Mirrors the backend `GuardrailOutcome` literal (`guardrail_schemas.py`). Declared as a const
 * tuple rather than a bare union so the runtime guard below can be derived from it: a hand-written
 * `=== 'warn' || === 'block'` check silently rejected any outcome added later, and a rejected
 * violation takes the whole `guardrails` body down with it (see `guardrailsError.ts`).
 */
export const GUARDRAIL_OUTCOMES = ['warn', 'block', 'route_to_human'] as const;

export type GuardrailOutcome = (typeof GUARDRAIL_OUTCOMES)[number];

/**
 * The outcomes that stop the action outright — everything an override reason cannot resolve.
 * `block` and `route_to_human` differ in who can satisfy the rule, not in whether this attempt
 * proceeds, so both render as a hard stop.
 */
const BLOCKING_OUTCOMES: readonly GuardrailOutcome[] = ['block', 'route_to_human'];

export const isBlockingOutcome = (type: GuardrailOutcome): boolean => BLOCKING_OUTCOMES.includes(type);

export const isGuardrailOutcome = (value: unknown): value is GuardrailOutcome =>
  typeof value === 'string' && (GUARDRAIL_OUTCOMES as readonly string[]).includes(value);

/** A single triggered guardrail (error body `guardrails.violations[]`). */
export interface GuardrailViolation {
  key: string;
  type: GuardrailOutcome;
  message: string;
}

/** The `guardrails` object inside a 409/403 error response body. */
export interface GuardrailsErrorBody {
  is_blocked: boolean;
  violations: GuardrailViolation[];
  /**
   * Correlation id for the whole user attempt, minted by the backend when a rule trips.
   * Optional: absent from an older backend, and `null` when the telemetry seam could not mint
   * one — neither is an error, it only costs that attempt its correlation.
   */
  attempt_id?: string;
}

/**
 * JSON payload of the `X-Guardrail-Override` request header sent on replay.
 * `tripped_at` is the client's ISO-8601 timestamp of when it received the 409
 * (the backend reads it via `Date.toISOString()` — see the evaluator's
 * `_as_aware_utc`), NOT a value echoed from the response body.
 */
export interface GuardrailOverrideToken {
  override_reason: string;
  acknowledged_keys: string[];
  tripped_at: string;
  /**
   * Echoed back so every replay round of one user intent reports as one attempt. Optional, and
   * omitted from the serialized header when absent: `JSON.stringify` drops an `undefined`
   * property, which is what the backend's `extra='forbid'` token wants to see rather than a
   * `null` it has to special-case.
   */
  attempt_id?: string;
}

/** What the user's dialog decision resolves to. */
export interface GuardrailsDialogResult {
  proceed: boolean;
  reason: string;
}

/**
 * What `openGuardrailsDialog` resolves: the user's decision plus whether a dialog was
 * actually presented. `dialogShown` is false only in the degraded fallback where no host
 * is mounted — the interceptor uses it to decide whether to suppress the generic error
 * popup (suppress only when the user actually saw and dismissed the dialog).
 */
export interface GuardrailsDialogOutcome extends GuardrailsDialogResult {
  dialogShown: boolean;
}
